import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Zap, Coins, ExternalLink, RefreshCw, CheckCircle2, XCircle, Info } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import TopUpDialog from '../credits/TopUpDialog';
import CreditValueInfo from '../CreditValueInfo';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';

// Human labels for AI-credit ledger grant/purchase actions (for the breakdown tooltip).
const CREDIT_SOURCE_LABELS = {
  free_plan_signup: 'Free signup welcome',
  starter_welcome_grant: 'Starter welcome bonus',
  starter_monthly_grant: 'Monthly plan credits',
  growth_welcome_grant: 'Growth welcome bonus',
  growth_monthly_grant: 'Monthly plan credits',
  enterprise_welcome_grant: 'Enterprise welcome bonus',
  enterprise_monthly_grant: 'Monthly plan credits',
  super_admin_grant: 'Support team grant',
};
const creditSourceLabel = (e) =>
  CREDIT_SOURCE_LABELS[e.action] ||
  (e.feature === 'admin_topup' ? 'Support team grant'
    : e.feature === 'org_wallet_topup' || e.kind === 'purchase' ? 'Top-up purchase'
    : e.feature === 'subscription_grant' ? 'Plan credits'
    : e.action || e.feature || 'Credit');

const SUBS = [
  { id: 'bill_subscription', label: 'Bill Subscription' },
  { id: 'bill_credits', label: 'Bill Credits' },
  { id: 'astrid_credits', label: 'Astrid, Your AI Support Assistant Credits' },
];

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Format AI credits so fractional Astrid usage (metered per exact token) is shown
// accurately — up to 2 decimals, trailing zeros trimmed (12 → "12", 0.4 → "0.4").
const fmtCredits = (n) => {
  const v = Math.max(0, Number(n || 0));
  return Number(v.toFixed(2)).toString();
};

export default function BillingSettings({ currentAccount, initialSub }) {
  const navigate = useNavigate();
  const acctId = currentAccount?.id;
  const [sub, setSub] = useState(initialSub || 'bill_subscription');

  const [snap, setSnap] = useState(null);
  const [bcWallet, setBcWallet] = useState(null);
  const [bcPackages, setBcPackages] = useState([]);
  const [bcLedger, setBcLedger] = useState({ entries: [], summary: {} });
  const [aiWallet, setAiWallet] = useState(null);
  const [aiLedger, setAiLedger] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [autoRc, setAutoRc] = useState(null);

  const load = useCallback(() => {
    if (!acctId) return;
    const p = { account_id: acctId };
    api.get('/subscription/current', { params: p }).then(({ data }) => setSnap(data)).catch(() => {});
    api.get('/billing/bill-credits/wallet', { params: p }).then(({ data }) => setBcWallet(data)).catch(() => {});
    api.get('/billing/bill-credits/packages', { params: p }).then(({ data }) => setBcPackages(data.packages || [])).catch(() => {});
    api.get('/billing/bill-credits/ledger', { params: p }).then(({ data }) => setBcLedger(data)).catch(() => {});
    api.get('/credits/wallet', { params: p }).then(({ data }) => setAiWallet(data)).catch(() => {});
    api.get('/credits/ledger', { params: { ...p, limit: 200 } }).then(({ data }) => setAiLedger(data.entries || [])).catch(() => {});
    api.get('/billing/invoices', { params: p }).then(({ data }) => setInvoices(data.invoices || [])).catch(() => {});
    api.get('/billing/bill-credits/auto-recharge', { params: p }).then(({ data }) => setAutoRc(data)).catch(() => {});
  }, [acctId]);

  useEffect(() => { load(); }, [load]);

  const saveAutoRc = async (patch) => {
    const next = { ...(autoRc || { enabled: false, threshold_credits: 5, topup_usd: 20 }), ...patch };
    setAutoRc(next);
    try {
      const { data } = await api.put('/billing/bill-credits/auto-recharge', {
        enabled: next.enabled, threshold_credits: Number(next.threshold_credits), topup_usd: Number(next.topup_usd),
      }, { params: { account_id: acctId } });
      setAutoRc(data);
    } catch { toast.error('Could not save auto-recharge'); }
  };

  const buyBillCredits = async (packageId, amountUsd) => {
    setBusy(true);
    try {
      const body = { origin_url: window.location.origin };
      if (packageId) body.package_id = packageId; else body.amount_usd = Number(amountUsd);
      const { data } = await api.post('/billing/bill-credits/checkout', body, { params: { account_id: acctId } });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    } finally { setBusy(false); }
  };

  const cancelPlan = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current period?')) return;
    try { await api.post('/subscription/cancel', { account_id: acctId, at_period_end: true }); toast.success('Subscription will cancel at period end'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };
  const resumePlan = async () => {
    try { await api.post('/subscription/resume', { account_id: acctId }); toast.success('Subscription resumed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Resume failed'); }
  };
  const updatePaymentMethod = async () => {
    try {
      const { data } = await api.get('/subscription/portal-link', { params: { account_id: acctId } });
      if (data?.url) { window.location.href = data.url; return; }
      toast.info('A secure card-management portal opens once your plan is on a live Stripe account.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not open billing portal'); }
  };

  const openInvoice = async (inv) => {
    if (inv.hosted_invoice_url) { window.open(inv.hosted_invoice_url, '_blank'); return; }
    try {
      const res = await api.get(`/billing/invoice/${inv.stream}/${encodeURIComponent(inv.ref)}`,
        { params: { account_id: acctId }, responseType: 'text' });
      const blob = new Blob([res.data], { type: 'text/html' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Could not open invoice'); }
  };

  const plan = snap?.plan || {};
  const subscription = snap?.subscription || {};
  const bill = snap?.usage?.bill_credits || {};
  const isFree = plan.id === 'free';
  const cancelAtEnd = subscription?.cancel_at_period_end;

  const filteredInvoices = (stream) => invoices.filter((i) => i.stream === stream);

  return (
    <div data-testid="billing-settings">
      {/* Sub-tab nav */}
      <div className="flex flex-wrap gap-1 border-b border-border mb-6">
        {SUBS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`billing-subtab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              sub === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <button onClick={load} className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="billing-refresh">
        <RefreshCw className="h-3 w-3" /> Refresh
      </button>

      {/* ── BILL SUBSCRIPTION ─────────────────────────────────────────── */}
      {sub === 'bill_subscription' && (
        <div className="space-y-6" data-testid="billing-bill-subscription">
          <div className="border border-border p-6 flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Current plan</div>
              <div className="text-3xl font-black tracking-tighter" data-testid="billing-plan-name">{plan.label || 'Free'}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {plan.usd_per_month ? `${usd(plan.usd_per_month)}/mo` : 'Free forever'}
              </div>
              <div className="mt-3 text-sm">
                {isFree ? (
                  <span className="text-muted-foreground">{bill.remaining ?? 0} of {bill.allowance ?? 5} free bill credits left this month · 1 credit = one account on a bill</span>
                ) : subscription?.current_period_end ? (
                  <span className="inline-flex items-center gap-1.5">
                    {cancelAtEnd
                      ? <><XCircle className="h-4 w-4 text-amber-500" /> Cancels on {fmtDate(subscription.current_period_end)}</>
                      : <><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Auto-renews on {fmtDate(subscription.current_period_end)}</>}
                  </span>
                ) : null}
              </div>
              {subscription?.status && (
                <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">Status: {subscription.status}</div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/billing/choose')} className="rounded-none font-bold uppercase tracking-wide text-xs" data-testid="billing-manage-plan">
                <CreditCard className="h-4 w-4 mr-2" />{isFree ? 'Upgrade' : 'Change plan'}
              </Button>
              {!isFree && (
                <Button onClick={updatePaymentMethod} variant="outline" className="rounded-none font-semibold text-xs" data-testid="billing-update-card">
                  <CreditCard className="h-4 w-4 mr-2" />Update payment method
                </Button>
              )}
              {!isFree && !cancelAtEnd && (
                <button onClick={cancelPlan} className="text-xs text-rose-600 hover:underline" data-testid="billing-cancel-plan">Cancel subscription</button>
              )}
              {!isFree && cancelAtEnd && (
                <button onClick={resumePlan} className="text-xs text-emerald-600 hover:underline" data-testid="billing-resume-plan">Resume subscription</button>
              )}
            </div>
          </div>

          <InvoiceTable title="Subscription invoices" rows={filteredInvoices('subscription')} onOpen={openInvoice} testid="subscription" />
        </div>
      )}

      {/* ── BILL CREDITS ──────────────────────────────────────────────── */}
      {sub === 'bill_credits' && (
        <div className="space-y-6" data-testid="billing-bill-credits">
          <div className="border border-emerald-200 bg-emerald-50 rounded-md px-4 py-3 text-sm text-emerald-900" data-testid="bc-explainer">
            <span className="font-semibold">How credits work:</span> 1 credit = <span className="font-semibold">one account on a bill</span>. A single-account bill = 1 credit; a bill covering 3 accounts uses 3 credits. Each credit isn&apos;t just parsing — it unlocks the whole platform for that account (collection, validation, ENERGY STAR, payment files &amp; full lifecycle). Free plan includes 5 credits/month; buy more anytime at $3.25 each (they roll over).
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Free credits left this month" value={bcWallet?.remaining_free ?? '—'} sub={`of ${bcWallet?.monthly_allowance ?? 5}`} />
            <Stat label="Pre-paid credits" value={Math.max(0, Math.round(bcWallet?.purchased_credits ?? 0))} sub={usd(bcWallet?.purchased_usd_value)} />
            <Stat label="Bills parsed" value={bcLedger?.summary?.bills_parsed ?? 0} sub="all time" />
            <Stat label="Accounts billed" value={bcLedger?.summary?.accounts_commodity_billed ?? 0} sub="= credits used" />
          </div>

          <div className="border border-border p-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Top up bill credits</div>
            <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5">
              1 credit = one account on a bill = {usd(bcWallet?.unit_usd || 3.25)}. Credits roll over and never expire.
              <CreditValueInfo unitUsd={bcWallet?.unit_usd || 3.25} />
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {bcPackages.map((p) => (
                <button key={p.id} disabled={busy} onClick={() => buyBillCredits(p.id)} data-testid={`bc-pkg-${p.id}`}
                  className="border border-border rounded-lg px-4 py-3 text-left hover:border-primary hover:bg-muted transition-colors disabled:opacity-50">
                  <div className="text-lg font-bold">{p.label}</div>
                  <div className="text-xs text-muted-foreground">≈ {p.units} credits</div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input type="number" min="5" step="1" value={custom} onChange={(e) => setCustom(e.target.value)}
                placeholder="Custom amount (min $5)" data-testid="bc-custom-amount"
                className="w-48 rounded-md border border-border px-3 py-2 text-sm" />
              <Button disabled={busy || !custom || Number(custom) < 5} onClick={() => buyBillCredits(null, custom)} data-testid="bc-custom-buy"
                className="rounded-md font-semibold text-xs">
                <Zap className="h-4 w-4 mr-1" /> Buy credits
              </Button>
            </div>
          </div>

          <div className="border border-border p-6" data-testid="auto-recharge-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Auto recharge</div>
                <p className="text-sm text-muted-foreground">Never pause processing — automatically buy more bill credits when you run low.</p>
              </div>
              <label className="inline-flex items-center cursor-pointer" data-testid="auto-recharge-toggle">
                <input type="checkbox" className="sr-only peer" checked={!!autoRc?.enabled}
                  onChange={(e) => saveAutoRc({ enabled: e.target.checked })} />
                <div className="relative w-11 h-6 bg-slate-200 peer-checked:bg-emerald-600 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:h-5 after:w-5 after:rounded-full after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>
            {autoRc?.enabled && (
              <div className="mt-4 flex flex-wrap items-end gap-4">
                <label className="text-sm">
                  <span className="block text-xs text-muted-foreground mb-1">When balance drops below</span>
                  <div className="flex items-center gap-1">
                    <input type="number" min="0" step="1" value={autoRc?.threshold_credits ?? 5}
                      onChange={(e) => setAutoRc({ ...autoRc, threshold_credits: e.target.value })}
                      onBlur={(e) => saveAutoRc({ threshold_credits: e.target.value })}
                      data-testid="auto-recharge-threshold"
                      className="w-24 rounded-md border border-border px-3 py-2 text-sm" />
                    <span className="text-sm text-muted-foreground">credits</span>
                  </div>
                </label>
                <label className="text-sm">
                  <span className="block text-xs text-muted-foreground mb-1">Automatically buy</span>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">$</span>
                    <input type="number" min="5" step="1" value={autoRc?.topup_usd ?? 20}
                      onChange={(e) => setAutoRc({ ...autoRc, topup_usd: e.target.value })}
                      onBlur={(e) => saveAutoRc({ topup_usd: e.target.value })}
                      data-testid="auto-recharge-amount"
                      className="w-24 rounded-md border border-border px-3 py-2 text-sm" />
                    <span className="text-sm text-muted-foreground">≈ {Math.round((Number(autoRc?.topup_usd ?? 20)) / (bcWallet?.unit_usd || 3.25))} credits</span>
                  </div>
                </label>
              </div>
            )}
            {autoRc?.enabled && !autoRc?.has_saved_card && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="auto-recharge-note">
                Automatic charging needs a saved card. In sandbox {"we'll"} prompt you to top up when the balance hits your threshold; add a card on file (available with a live payment key) to charge automatically.
              </p>
            )}
          </div>

          <UsageLedger entries={bcLedger?.entries || []} />
          <InvoiceTable title="Bill-credit invoices" rows={filteredInvoices('bill_credits')} onOpen={openInvoice} testid="bill_credits" />
        </div>
      )}

      {/* ── ASTRID CREDITS ────────────────────────────────────────────── */}
      {sub === 'astrid_credits' && (
        <div className="space-y-6" data-testid="billing-astrid-credits">
          <div className="border border-border p-6 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                Astrid AI credits
                <CreditBreakdown ledger={aiLedger} wallet={aiWallet} />
              </div>
              <div className="text-3xl font-black tracking-tighter" data-testid="astrid-balance">
                {fmtCredits(aiWallet?.balance_credits ?? 0)} <span className="text-base font-medium text-muted-foreground">credits</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">{usd(aiWallet?.balance_usd)} · powers Astrid, reports, vendor emails & portal setup</div>
            </div>
            <Button onClick={() => setTopupOpen(true)} className="rounded-none font-bold uppercase tracking-wide text-xs" data-testid="astrid-topup">
              <Coins className="h-4 w-4 mr-2" /> Top up
            </Button>
          </div>
          <InvoiceTable title="Astrid credit invoices" rows={filteredInvoices('ai_credits')} onOpen={openInvoice} testid="ai_credits" />
          <TopUpDialog open={topupOpen} onOpenChange={setTopupOpen} wallet={aiWallet} onOpened={load} />
        </div>
      )}
    </div>
  );
}

function CreditBreakdown({ ledger, wallet }) {
  const grants = (ledger || []).filter((e) => e.kind === 'credit' && Number(e.credits) > 0);
  const groups = {};
  grants.forEach((e) => {
    const key = creditSourceLabel(e);
    groups[key] = (groups[key] || 0) + Number(e.credits || 0);
  });
  const rows = Object.entries(groups).sort((a, b) => b[1] - a[1]);
  const totalGranted = grants.reduce((s, e) => s + Number(e.credits || 0), 0);
  const consumed = Number(wallet?.consumed_credits ?? 0);
  const balance = Math.max(0, Number(wallet?.balance_credits ?? 0));

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        <button type="button" className="inline-flex text-muted-foreground hover:text-foreground" data-testid="astrid-credit-breakdown-trigger" aria-label="How you got these credits">
          <Info className="h-3.5 w-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-72" data-testid="astrid-credit-breakdown">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">How you got these credits</div>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No credit grants recorded yet.</p>
        ) : (
          <div className="space-y-1.5">
            {rows.map(([label, amt]) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-semibold text-emerald-600">+{fmtCredits(amt)}</span>
              </div>
            ))}
            <div className="border-t border-border my-1.5" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total added</span>
              <span className="font-semibold">{fmtCredits(totalGranted)}</span>
            </div>
            {consumed > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Used by Astrid</span>
                <span className="font-semibold text-rose-600">-{fmtCredits(consumed)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm font-bold">
              <span>Current balance</span>
              <span>{fmtCredits(balance)}</span>
            </div>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-border p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function UsageLedger({ entries }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Bill-credit usage log</div>
      <div className="border border-border" data-testid="bc-usage-ledger">
        {entries.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">No usage yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                <th className="text-left px-4 py-2.5 font-semibold">Activity</th>
                <th className="text-left px-4 py-2.5 font-semibold">Detail</th>
                <th className="text-right px-4 py-2.5 font-semibold">Credits</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(e.ts)}</td>
                  <td className="px-4 py-2.5">{e.kind === 'purchase' ? 'Top-up' : 'Bill parsed'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {e.kind === 'purchase'
                      ? e.description
                      : `${e.filename || 'Bill'}${e.commodity ? ` · ${e.commodity}` : ''}${e.accounts ? ` · ${e.accounts} acct` : ''}`}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-semibold ${e.kind === 'purchase' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {e.kind === 'purchase' ? `+${e.units}` : `-${e.units}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InvoiceTable({ title, rows, onOpen, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">{title}</div>
      <div className="border border-border" data-testid={`invoices-${testid}`}>
        {rows.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">No invoices yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Invoice</th>
                <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                <th className="text-left px-4 py-2.5 font-semibold">Description</th>
                <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={`${inv.stream}-${inv.number}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 font-medium">{inv.number}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(inv.date)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{inv.description}</td>
                  <td className="px-4 py-2.5 text-right">{usd(inv.amount_usd)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => onOpen(inv)} className="text-primary hover:underline inline-flex items-center gap-1">
                      View <ExternalLink className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
